import { openConsentPreferences } from "../../../../utils/consent";
import { ShieldCheck } from "lucide-react";
// UserMenu Component
// LazySEM Frontend - User avatar and dropdown menu

import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import type { User } from "../../../../contexts/AuthContext";
import styles from "./UserMenu.module.css";

interface UserMenuProps {
  user: User;
  onAction: (action: string) => void;
  className?: string;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  onAction,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleAction = (action: string) => {
    setIsOpen(false);
    onAction(action);
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    return user.name;
  };

  return (
    <div className={`${styles.userMenu} ${className}`} ref={menuRef}>
      {/* User Avatar Button */}
      <button
        onClick={toggleMenu}
        className={styles.avatarButton}
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className={styles.avatar}>
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={getUserDisplayName()}
              className={styles.avatarImage}
            />
          ) : (
            <div className={styles.avatarInitials}>
              {getInitials(user.name)}
            </div>
          )}
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={styles.dropdown}>
          {/* User Info */}
          <div className={styles.userInfo}>
            <div className={styles.userName}>{getUserDisplayName()}</div>
            <div className={styles.userEmail}>{user.email}</div>
            <div className={styles.userRole}>{user.role}</div>
          </div>

          <div className={styles.divider} />

          {/* Menu Items */}
          <div className={styles.menuItems}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setIsOpen(false);
                openConsentPreferences();
              }}
            >
              <ShieldCheck size={16} aria-hidden="true" />
              Privacy preferences
            </button>
            <Link
              to="/profile"
              className={styles.menuItem}
              onClick={() => handleAction("profile")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Profile
            </Link>

            <Link
              to="/settings"
              className={styles.menuItem}
              onClick={() => handleAction("settings")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="m12 1 2.09 5.26L22 9l-5.26 2.09L14 22l-2.09-5.26L4 14l5.26-2.09L12 1z" />
              </svg>
              Settings
            </Link>

            <Link
              to="/settings/security"
              className={styles.menuItem}
              onClick={() => handleAction("security")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Security
            </Link>

            <Link
              to="/settings/tokens"
              className={styles.menuItem}
              onClick={() => handleAction("tokens")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="7.5" cy="15.5" r="5.5" />
                <path d="m21 2-9.6 9.6" />
                <path d="m15.5 7.5 3 3L22 7l-3-3" />
              </svg>
              Access Tokens
            </Link>

            {/* Admin Link - Only show for admin users */}
            {(user.role === "admin" || user.role === "owner") && (
              <Link
                to="/admin"
                className={styles.menuItem}
                onClick={() => handleAction("admin")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 1l3 6 6 .75-4.5 4.25L18 19l-6-3.25L6 19l1.5-6.75L3 7.75 9 7l3-6z" />
                </svg>
                Admin
              </Link>
            )}

            <Link
              to="/help"
              className={styles.menuItem}
              onClick={() => handleAction("help")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Help & Support
            </Link>
          </div>

          <div className={styles.divider} />

          {/* Logout */}
          <button
            onClick={() => handleAction("logout")}
            className={`${styles.menuItem} ${styles.logoutItem}`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
